<template>
  <div class="relative hidden text-white sm:flex">
    <div
      id="app-navigation"
      :class="[
        'flex h-screen flex-col overflow-x-hidden bg-[#0A020A] transition-all duration-300',
        navCollapsed && 'collapsed',
        'bg-left-bottom',
        'bg-no-repeat',
        { 'bg-[length:100%_auto]': !navCollapsed },
      ]"
      :style="{ backgroundImage: `url(${NavigationPanelImage})` }"
      data-cy="menu-app-navigation"
    >
      <!-- START: Sticky top section -->
      <div class="bg-brand-background-dark sticky top-0 z-10 px-2 pt-2 pb-3">
        <Logo v-if="!navCollapsed" class="mx-auto mt-4 h-24 transition-all duration-300" />
        <LogoCollapsed v-else class="mt-4 mr-2 h-24 transition-all duration-300" />

        <!-- nav collapse toggle -->
        <button
          class="absolute top-1 right-1 flex h-8 w-8 items-center rounded bg-[#262626] p-2 text-white"
          data-cy="nav-collapse-toggle-button"
          @click="userPreferencesStore.setNavTogglePreference(!navCollapsed)"
        >
          <ChevronDoubleRightIcon v-if="navCollapsed" />
          <ChevronDoubleLeftIcon v-else />
        </button>

        <hr
          :class="[
            navCollapsed ? 'w-10' : 'w-11/12',
            'border-border-border-700 3xl:mb-6 mx-auto mb-2 border transition-all duration-300',
          ]"
        />

        <!-- org select -->
        <div class="relative h-16 transition-all duration-300">
          <OrganisationSwitcherMenu
            v-if="isMultiOrg"
            v-model:open="orgsMenuOpen"
            :orgs="orgs"
            :selected-org-id="selectedOrgId"
            :show-create-new="showCreateNew"
            :create-new-label="createNewLabel"
            @select-org="handleSelectOrg"
            @set-primary="onSetPrimary"
            @create-new="handleCreateNew"
          >
            <template #trigger>
              <button
                type="button"
                class="flex h-full w-full cursor-pointer items-center rounded-lg px-4 transition-all duration-300 hover:bg-white/10"
                :class="{ 'justify-center': navCollapsed, 'justify-start': !navCollapsed }"
                aria-haspopup="menu"
                :aria-expanded="orgsMenuOpen"
                data-cy="organisation-switcher-trigger"
              >
                <OrgAssetDisplay
                  :logo-url="logoUrl"
                  :org-name="orgName"
                  :nav-collapsed="navCollapsed"
                />

                <div v-if="!navCollapsed" class="flex min-w-0 flex-1 items-center justify-between">
                  <span class="type-body-1-strong w-full truncate text-left">{{ orgName }}</span>

                  <ChevronDownIcon
                    :class="[
                      orgsMenuOpen ? 'rotate-180' : '',
                      'ml-1 w-4 min-w-4 shrink-0 stroke-2 transition-transform',
                    ]"
                  />
                </div>
              </button>
            </template>
          </OrganisationSwitcherMenu>

          <div
            v-else
            class="flex h-full w-full cursor-default items-center px-4 transition-all duration-300"
            :class="{ 'justify-center': navCollapsed, 'justify-start': !navCollapsed }"
          >
            <OrgAssetDisplay
              :logo-url="logoUrl"
              :org-name="orgName"
              :nav-collapsed="navCollapsed"
            />

            <div v-if="!navCollapsed" class="flex min-w-0 flex-1 items-center justify-between">
              <span class="type-body-1-strong w-full truncate text-center">{{ orgName }}</span>
            </div>
          </div>
        </div>

        <hr
          :class="[
            navCollapsed ? 'w-8' : 'w-11/12',
            'border-border-border-700 3xl:mb-4 3xl:mt-4 mx-auto mt-2 mb-2 border transition-all duration-30',
          ]"
        />
      </div>

      <!-- MIDDLE: Scrollable menu items -->
      <div
        ref="scrollContainer"
        class="custom-scrollbar flex-1 overflow-x-hidden overflow-y-auto pr-1"
        style="scrollbar-gutter: stable"
      >
        <div v-for="item in navItems" :key="item.key" class="mb-4 ml-2">
          <Tooltip :label="item.label" position="right" :is-disabled="!navCollapsed">
            <router-link
              v-if="item.route && item.visible"
              :to="item.route"
              :data-testId="`${item.label}-nav-option`"
              :class="[navItemClasses, { 'router-link-active': isAlsoActive(item) }]"
            >
              <component :is="getNavIcon(item)" :class="getIconClasses(item)" />
              <div class="nav-label type-body-1">{{ item.label }}</div>

              <NotificationBadge
                v-if="getNotificationCount(item) > 0"
                :count="getNotificationCount(item)"
                :collapsed="navCollapsed"
              />
            </router-link>
          </Tooltip>
        </div>
      </div>

      <!-- END: Sticky bottom section -->
      <div class="bg-brand-background-dark sticky bottom-0 z-10 px-2 pt-3 pb-5">
        <NavFeedbackLink :collapsed="navCollapsed" />
        <DropdownMenu
          :items="accountMenuItems"
          size="comfortable"
          @open-change="accountMenuOpen = $event"
        >
          <template #trigger>
            <button
              type="button"
              class="flex w-full rounded-md bg-white/20 p-2"
              aria-haspopup="menu"
              :aria-expanded="accountMenuOpen"
              data-cy="account-menu-trigger"
            >
              <div class="relative">
                <Avatar :label="userName" />
                <div class="absolute -right-1 -bottom-0 rounded-full bg-white p-0.5 text-black">
                  <Cog6ToothIcon class="w-4" />
                </div>
              </div>
              <div
                v-if="!navCollapsed"
                class="3xl:w-full 3xl:max-w-[150px] ml-2 max-w-[120px] text-left"
              >
                <p class="type-body-1 truncate">{{ userFirstName }}</p>
                <p class="type-body-2 truncate">{{ userEmail }}</p>
              </div>
            </button>
          </template>
        </DropdownMenu>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import router from '@/router';
import i18n from '@/plugins/vue-i18n';
import { computed, onMounted, ref } from 'vue';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
} from '@heroicons/vue/24/outline';

import { storeToRefs } from 'pinia';

import { Avatar, DropdownMenu, Tooltip } from '@boqnet/ui';
import Logo from '@/components/Brand/Logo.vue';
import LogoCollapsed from '@/components/Brand/LogoCollapsed.vue';
import OrgAssetDisplay from '@/components/Navigation/OrgAssetDisplay.vue';
import NotificationBadge from '@/components/Navigation/NotificationBadge.vue';
import NavFeedbackLink from '@/components/Navigation/NavFeedbackLink.vue';

import type { NavMenuItem } from '@/types/components/navigation.types';
import { loggedInUserOrgList } from '@/common/utils/role.utils';
import { useUserPreferencesStore } from '@/stores/user-preferences.store';
import { useOrganisationSwitcher } from '@/composables/use-organisation-switcher';
import { useAccountMenuItems } from '@/composables/use-account-menu-items';
import OrganisationSwitcherMenu from '@/components/Navigation/OrganisationSwitcher/OrganisationSwitcherMenu.vue';

import NavigationPanelImage from '@/components/Brand/DefaultLayout/NavigationPanelAsset.svg';

const props = defineProps<{
  navItems: NavMenuItem[];
  orgName: string;
  userName: string;
  userFirstName: string;
  userEmail: string;
  paymentRequestsCount: number;
  creditsCount?: number;
  orderRequestsCount: number;
  claimRequestsCount: number;
  returnRequestsCount: number;
  clientVariationCount: number;
  logoUrl?: string;
  showSettingsItems: boolean;
}>();

const userPreferencesStore = useUserPreferencesStore();

const { navCollapsed } = storeToRefs(userPreferencesStore);

const { t } = i18n.global;

const { items: accountMenuItems } = useAccountMenuItems(computed(() => props.showSettingsItems));
const accountMenuOpen = ref(false);

const orgsMenuOpen = ref(false);

const {
  orgs,
  selectedOrgId,
  showCreateNew,
  createNewLabel,
  onSelectOrg,
  onSetPrimary,
  onCreateNew,
} = useOrganisationSwitcher();

const handleSelectOrg = async (org: Parameters<typeof onSelectOrg>[0]) => {
  await onSelectOrg(org);
  orgsMenuOpen.value = false;
};

const handleCreateNew = () => {
  onCreateNew();
  orgsMenuOpen.value = false;
};

const orderAndReturnRequestsCount = computed(
  () => props.orderRequestsCount + props.returnRequestsCount,
);

// Accounts badge = pending payment requests + pending credits (credits only supplied
// for roles whose Accounts surface includes the Credits tab; defaults to 0). Mirrors
// the orders+returns sum pattern above. (PE-213)
const accountsRequestsCount = computed(
  () => props.paymentRequestsCount + (props.creditsCount ?? 0),
);

const isMultiOrg = computed(() => loggedInUserOrgList.value.length > 1);

const navItemClasses = [
  // Base styles
  'inline-flex',
  'h-10',
  '3xl:h-[53px]',
  'w-full',
  'items-center',
  'border',
  'border-transparent',
  'px-2',
  // Hover styles
  'hover:bg-brand-greyscale-neutral-900',
  'hover:rounded-lg',
  // Active styles are in the scoped CSS (.router-link-active)
];

// Icon styling helper
const getIconClasses = (item: NavMenuItem) => {
  const isActive = isItemActive(item);

  return [
    'h-5',
    'min-w-5',
    isActive ? 'text-brand-primary-orange' : 'text-white',
    navCollapsed.value ? 'mx-auto' : 'mr-4',
  ];
};

const getNavIcon = (item: NavMenuItem) => {
  return isItemActive(item) ? item.activeIcon : item.icon;
};

const notificationCounts = computed(() => ({
  [t('routes.accounts.accountsPageTitle')]: accountsRequestsCount.value,
  [t('routes.orders.ordersPageTitle')]: orderAndReturnRequestsCount.value,
  [t('routes.clients.navigationLabel')]: props.clientVariationCount,
  [t('routes.orders.claimsPageTitle')]: props.claimRequestsCount,
}));

const getNotificationCount = (item: NavMenuItem): number => {
  return notificationCounts.value[item.label] || 0;
};

const activePage = (routeName: {
  name: string;
  hash?: string;
  params?: Record<string, string | undefined>;
}): boolean => {
  if (!routeName.name) return false;

  const resolved = router.resolve(routeName);
  const currentPath = router.currentRoute.value.path;

  return currentPath === resolved.path || currentPath.startsWith(resolved.path + '/');
};

// router-link only marks its own route active, so an entry's other pages are marked here.
const isAlsoActive = (item: NavMenuItem): boolean =>
  !!item.alsoActiveOn?.includes(router.currentRoute.value.name as string);

const isItemActive = (item: NavMenuItem): boolean => activePage(item.route) || isAlsoActive(item);

onMounted(() => {
  userPreferencesStore.loadFromLocalStorage();
});
</script>

<style scoped>
@reference "../../../assets/css/index.css";

#app-navigation .router-link-active {
  @apply border-border-border-400 rounded-lg bg-white/10;
}

#app-navigation.collapsed {
  width: 65px;
}

#app-navigation {
  width: 260px;
  transition: width 0.3s ease;
}

#app-navigation .menu-item,
#app-navigation.collapsed .menu-item a,
#app-navigation.collapsed .menu-item svg {
  @apply transition-all duration-300;
}

#app-navigation.collapsed .menu-item {
  padding-left: 0;
  width: 45px;
}

#app-navigation.collapsed .menu-item a {
  padding: 0;
  padding-left: 8px;
  padding-right: 6px;
}

#app-navigation.collapsed .menu-item svg {
  margin-right: 0;
}

#app-navigation .nav-label {
  @apply whitespace-nowrap opacity-100 transition-all duration-300;
}

#app-navigation.collapsed .nav-label {
  opacity: 0;
  width: 0;
}

:deep(.custom-scrollbar::-webkit-scrollbar) {
  width: 4px !important;
  height: 6px !important;
}

:deep(.custom-scrollbar::-webkit-scrollbar-track) {
  background: black;
}

:deep(.custom-scrollbar::-webkit-scrollbar-thumb) {
  background: #787582;
  border-radius: 15px;
  border: none;
}

:deep(.custom-scrollbar::-webkit-scrollbar-thumb:hover) {
  background: #787582;
}

:deep(.custom-scrollbar::-webkit-scrollbar) {
  visibility: hidden;
}

:deep(.custom-scrollbar.scrolling::-webkit-scrollbar) {
  visibility: visible;
}
</style>
